import React, { useState } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faInfoCircle, faEye, faEyeSlash, faSpinner, faKey, faCheck } from '@fortawesome/free-solid-svg-icons';
import styles from '../../styles/common/AuthPickupModal.module.css';
import modalStyles from '../../styles/common/Modal.module.css';
import { customerRecoveryService } from '../../services/customer.recovery.service';
import Swal from 'sweetalert2';

interface AuthPickupModalProps {
    show: boolean;
    isExistingUser: boolean;
    userName?: string;
    userEmail?: string;
    onConfirm: (password: string) => void;
    onCancel: () => void;
}

const AuthPickupModal: React.FC<AuthPickupModalProps> = ({
    show,
    isExistingUser,
    userName,
    userEmail,
    onConfirm,
    onCancel,
}) => {
    const brandName = process.env.REACT_APP_BRAND_NAME || 'elPaket';
    const [password, setPassword] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [showPassword, setShowPassword] = useState(false);

    // Recovery Flow States
    const [recoveryStep, setRecoveryStep] = useState<'login' | 'enter-code' | 'reset-password'>('login');
    const [recoveryLoading, setRecoveryLoading] = useState(false);
    const [recoveryCode, setRecoveryCode] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
    const [recoveryErrors, setRecoveryErrors] = useState<Record<string, string | null>>({});

    const validatePassword = (value: string): string | null => {
        if (!value) return 'La contraseña es requerida';
        if (recoveryStep === 'reset-password' || !isExistingUser) {
            if (value.length < 6) return 'Debe tener al menos 6 caracteres';
            if (value.length > 16) return 'Debe tener máximo 16 caracteres';
            if (!/[a-z]/.test(value)) return 'Debe contener al menos una minúscula';
            if (!/[A-Z]/.test(value)) return 'Debe contener al menos una mayúscula';
            if (!/[0-9]/.test(value)) return 'Debe contener al menos un número';
            if (!/[^A-Za-z0-9]/.test(value)) return 'Debe contener al menos un carácter especial';
        }
        return null;
    };

    const handleLoginSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const validationError = validatePassword(password);
        if (validationError) {
            setError(validationError);
            return;
        }
        onConfirm(password);
    };

    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPassword(e.target.value);
        if (error) {
            setError(null);
        }
    };

    const handleStartRecovery = async () => {
        if (!userEmail) return;
        setRecoveryLoading(true);
        try {
            const response = await customerRecoveryService.sendRecoveryCode(userEmail);
            if (response.success) {
                setRecoveryStep('enter-code');
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: response.error || 'Error al enviar código de recuperación.',
                    confirmButtonColor: '#003893'
                });
            }
        } catch (err) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Ha ocurrido un error inesperado al enviar el código.',
                confirmButtonColor: '#003893'
            });
        } finally {
            setRecoveryLoading(false);
        }
    };

    const handleVerifyCodeSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!userEmail || recoveryCode.length !== 6) return;
        setRecoveryLoading(true);
        try {
            const response = await customerRecoveryService.verifyRecoveryCode(userEmail, recoveryCode);
            if (response.success) {
                Swal.fire({
                    icon: 'success',
                    title: 'Código Verificado',
                    text: 'Tu código ha sido verificado. Ahora puedes restablecer tu contraseña.',
                    confirmButtonColor: '#003893',
                    timer: 3000
                });
                setRecoveryStep('reset-password');
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: response.error || 'El código es inválido o ha expirado.',
                    confirmButtonColor: '#003893'
                });
            }
        } catch (err) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Error al verificar el código.',
                confirmButtonColor: '#003893'
            });
        } finally {
            setRecoveryLoading(false);
        }
    };

    const handleResetPasswordSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!userEmail) return;

        const valNewError = validatePassword(newPassword);
        const valConfirmError = !confirmNewPassword 
            ? 'Confirma tu contraseña' 
            : newPassword !== confirmNewPassword 
                ? 'Las contraseñas no coinciden' 
                : null;

        if (valNewError || valConfirmError) {
            setRecoveryErrors({
                newPassword: valNewError,
                confirmNewPassword: valConfirmError
            });
            return;
        }

        setRecoveryLoading(true);
        try {
            const response = await customerRecoveryService.resetPassword(userEmail, newPassword);
            if (response.success) {
                await Swal.fire({
                    icon: 'success',
                    title: 'Contraseña Restablecida',
                    text: 'Tu contraseña ha sido restablecida exitosamente. Ya puedes iniciar sesión.',
                    confirmButtonColor: '#003893'
                });
                // Reset recovery state and go back to login mode so they can try again
                setRecoveryStep('login');
                setPassword('');
                setNewPassword('');
                setConfirmNewPassword('');
                setRecoveryCode('');
                setRecoveryErrors({});
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: response.error || 'Error al restablecer la contraseña.',
                    confirmButtonColor: '#003893'
                });
            }
        } catch (err) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Error al restablecer la contraseña.',
                confirmButtonColor: '#003893'
            });
        } finally {
            setRecoveryLoading(false);
        }
    };

    const handleClose = () => {
        setPassword('');
        setError(null);
        setShowPassword(false);
        setRecoveryStep('login');
        setRecoveryCode('');
        setNewPassword('');
        setConfirmNewPassword('');
        setRecoveryErrors({});
        onCancel();
    };

    return (
        <Modal
            show={show}
            onHide={handleClose}
            centered
            className={styles.authModal}
            contentClassName={styles.scaleIn}
            animation={false}
        >
            <Modal.Header closeButton>
                <Modal.Title as="h3" className={`${styles.modalTitle} ${modalStyles.modalTitle}`}>
                    <FontAwesomeIcon icon={recoveryStep === 'login' ? faLock : faKey} className={styles.titleIcon} />
                    {recoveryStep === 'login' 
                        ? (isExistingUser 
                            ? (userName ? `Bienvenido de nuevo, ${userName}` : 'Bienvenido de nuevo') 
                            : 'Crea tu cuenta')
                        : recoveryStep === 'enter-code'
                            ? 'Verificar Código'
                            : 'Nueva Contraseña'
                    }
                </Modal.Title>
            </Modal.Header>

            {recoveryStep === 'login' && (
                <Form onSubmit={handleLoginSubmit}>
                    <Modal.Body>
                        <p className={styles.modalSubtitle}>
                            {isExistingUser
                                ? 'Por favor, introduce tu contraseña para continuar.'
                                : (userName 
                                    ? (
                                        <>
                                            ¡Te damos la bienvenida, {userName} {userEmail && <strong>({userEmail})</strong>}! Para crear tu cuenta en {brandName} y continuar con tu solicitud, elige una contraseña segura:
                                        </>
                                      )
                                    : `¡Te damos la bienvenida! Para crear tu cuenta en ${brandName} y continuar con tu solicitud, elige una contraseña segura:`)}
                        </p>

                        {!isExistingUser && (
                            <div className={styles.requirementsBox}>
                                <div className={styles.requirementsHeader}>
                                    <FontAwesomeIcon icon={faInfoCircle} />
                                    <span>Requisitos de contraseña:</span>
                                </div>
                                <ul className={styles.requirementsList}>
                                    <li className={password.length >= 6 && password.length <= 16 ? styles.valid : ''}>
                                        Entre 6 y 16 caracteres
                                    </li>
                                    <li className={/[a-z]/.test(password) ? styles.valid : ''}>
                                        Al menos una minúscula
                                    </li>
                                    <li className={/[A-Z]/.test(password) ? styles.valid : ''}>
                                        Al menos una mayúscula
                                    </li>
                                    <li className={/[0-9]/.test(password) ? styles.valid : ''}>
                                        Al menos un número
                                    </li>
                                    <li className={/[^A-Za-z0-9]/.test(password) ? styles.valid : ''}>
                                        Al menos un carácter especial
                                    </li>
                                </ul>
                            </div>
                        )}

                        <div className={styles.inputWrapper}>
                            <Form.Group>
                                <Form.Label className={styles.inputLabel}>Contraseña</Form.Label>
                                <div className={styles.passwordInputContainer}>
                                    <Form.Control
                                        type={showPassword ? 'text' : 'password'}
                                        value={password}
                                        onChange={handlePasswordChange}
                                        placeholder="Escribe tu contraseña aquí"
                                        className={error ? styles.isInvalid : ''}
                                        autoFocus
                                        maxLength={16}
                                    />
                                    <button
                                        type="button"
                                        className={styles.togglePassword}
                                        onClick={() => setShowPassword(!showPassword)}
                                    >
                                        <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                                    </button>
                                </div>
                                {error && <div className={styles.invalidFeedback}>{error}</div>}
                            </Form.Group>
                        </div>

                        {isExistingUser && (
                            <div className={styles.forgotPasswordLinkContainer}>
                                <button
                                    type="button"
                                    disabled={recoveryLoading}
                                    className={styles.forgotPasswordLink}
                                    onClick={handleStartRecovery}
                                >
                                    {recoveryLoading ? (
                                        <>
                                            <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                                            Enviando código...
                                        </>
                                    ) : (
                                        '¿Olvidaste tu contraseña?'
                                    )}
                                </button>
                            </div>
                        )}
                    </Modal.Body>

                    <Modal.Footer>
                        <Button className={styles.cancelButton} onClick={handleClose} type="button">
                            Cancelar
                        </Button>
                        <Button className={modalStyles.closeBtn} type="submit">
                            Continuar
                        </Button>
                    </Modal.Footer>
                </Form>
            )}

            {recoveryStep === 'enter-code' && (
                <Form onSubmit={handleVerifyCodeSubmit}>
                    <Modal.Body>
                        <p className={styles.modalSubtitle}>
                            Hemos enviado un código de verificación a tu correo <strong>{userEmail}</strong>. Por favor, ingrésalo a continuación.
                        </p>

                        <div className={styles.codeInputContainer}>
                            <input
                                id="recoveryCode"
                                name="recoveryCode"
                                type="text"
                                required
                                value={recoveryCode}
                                onChange={(e) => setRecoveryCode(e.target.value.replace(/\D/g, ''))}
                                className={styles.codeInputHidden}
                                maxLength={6}
                                autoComplete="one-time-code"
                                autoFocus
                            />
                            <div 
                                className={styles.codeInputBoxes}
                                onClick={() => document.getElementById('recoveryCode')?.focus()}
                                style={{ cursor: 'text' }}
                            >
                                {[...Array(6)].map((_, i) => (
                                    <div 
                                        key={i} 
                                        className={`${styles.codeInputBox} ${recoveryCode.length === i ? styles.codeInputBoxActive : ''}`}
                                    >
                                        {recoveryCode[i] || ''}
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div style={{ marginTop: "20px", fontSize: "13px", color: "#6b7280", textAlign: "center", lineHeight: "1.5" }}>
                            <p style={{ fontWeight: "700", margin: "0 0 6px 0", color: "#374151" }}>
                                ¿No recibiste el código de verificación?
                            </p>
                            <p style={{ margin: 0 }}>
                                Por favor, revisa tu bandeja de entrada o correo no deseado (spam). Si aún no lo has recibido, haz clic en{" "}
                                <button
                                    type="button"
                                    onClick={handleStartRecovery}
                                    disabled={recoveryLoading}
                                    className={styles.forgotPasswordLink}
                                    style={{
                                        background: "none",
                                        border: "none",
                                        padding: 0,
                                        cursor: "pointer",
                                        fontWeight: "700",
                                        textDecoration: "underline",
                                        color: "#0a0a0a",
                                        display: "inline",
                                        font: "inherit"
                                    }}
                                >
                                    Reenviar código
                                </button>{" "}
                                para recibir uno nuevo.
                            </p>
                        </div>
                    </Modal.Body>

                    <Modal.Footer>
                        <Button className={styles.cancelButton} onClick={() => setRecoveryStep('login')} type="button">
                            Atrás
                        </Button>
                        <Button className={modalStyles.closeBtn} type="submit" disabled={recoveryLoading || recoveryCode.length !== 6}>
                            {recoveryLoading ? (
                                <>
                                    <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                                    Verificando...
                                </>
                            ) : (
                                'Verificar Código'
                            )}
                        </Button>
                    </Modal.Footer>
                </Form>
            )}

            {recoveryStep === 'reset-password' && (
                <Form onSubmit={handleResetPasswordSubmit}>
                    <Modal.Body>
                        <p className={styles.modalSubtitle}>
                            Por favor, elige tu nueva contraseña de acceso.
                        </p>

                        <div className={styles.requirementsBox}>
                            <div className={styles.requirementsHeader}>
                                <FontAwesomeIcon icon={faInfoCircle} />
                                <span>Requisitos de contraseña:</span>
                            </div>
                            <ul className={styles.requirementsList}>
                                <li className={newPassword.length >= 6 && newPassword.length <= 16 ? styles.valid : ''}>
                                    Entre 6 y 16 caracteres
                                </li>
                                <li className={/[a-z]/.test(newPassword) ? styles.valid : ''}>
                                    Al menos una minúscula
                                </li>
                                <li className={/[A-Z]/.test(newPassword) ? styles.valid : ''}>
                                    Al menos una mayúscula
                                </li>
                                <li className={/[0-9]/.test(newPassword) ? styles.valid : ''}>
                                    Al menos un número
                                </li>
                                <li className={/[^A-Za-z0-9]/.test(newPassword) ? styles.valid : ''}>
                                    Al menos un carácter especial
                                </li>
                            </ul>
                        </div>

                        <div className={styles.inputWrapper}>
                            <Form.Group className="mb-3">
                                <Form.Label className={styles.inputLabel}>Nueva Contraseña</Form.Label>
                                <div className={styles.passwordInputContainer}>
                                    <Form.Control
                                        type={showNewPassword ? 'text' : 'password'}
                                        value={newPassword}
                                        onChange={(e) => {
                                            setNewPassword(e.target.value);
                                            setRecoveryErrors(prev => ({ ...prev, newPassword: null }));
                                        }}
                                        placeholder="Nueva contraseña"
                                        className={recoveryErrors.newPassword ? styles.isInvalid : ''}
                                        maxLength={16}
                                        autoFocus
                                    />
                                    <button
                                        type="button"
                                        className={styles.togglePassword}
                                        onClick={() => setShowNewPassword(!showNewPassword)}
                                    >
                                        <FontAwesomeIcon icon={showNewPassword ? faEyeSlash : faEye} />
                                    </button>
                                </div>
                                {recoveryErrors.newPassword && <div className={styles.invalidFeedback}>{recoveryErrors.newPassword}</div>}
                            </Form.Group>

                            <Form.Group>
                                <Form.Label className={styles.inputLabel}>Confirmar Contraseña</Form.Label>
                                <div className={styles.passwordInputContainer}>
                                    <Form.Control
                                        type={showConfirmNewPassword ? 'text' : 'password'}
                                        value={confirmNewPassword}
                                        onChange={(e) => {
                                            setConfirmNewPassword(e.target.value);
                                            setRecoveryErrors(prev => ({ ...prev, confirmNewPassword: null }));
                                        }}
                                        placeholder="Confirma tu nueva contraseña"
                                        className={recoveryErrors.confirmNewPassword ? styles.isInvalid : ''}
                                        maxLength={16}
                                    />
                                    <button
                                        type="button"
                                        className={styles.togglePassword}
                                        onClick={() => setShowConfirmNewPassword(!showConfirmNewPassword)}
                                    >
                                        <FontAwesomeIcon icon={showConfirmNewPassword ? faEyeSlash : faEye} />
                                    </button>
                                </div>
                                {recoveryErrors.confirmNewPassword && <div className={styles.invalidFeedback}>{recoveryErrors.confirmNewPassword}</div>}
                            </Form.Group>
                        </div>
                    </Modal.Body>

                    <Modal.Footer>
                        <Button className={styles.cancelButton} onClick={() => setRecoveryStep('enter-code')} type="button">
                            Atrás
                        </Button>
                        <Button className={modalStyles.closeBtn} type="submit" disabled={recoveryLoading}>
                            {recoveryLoading ? (
                                <>
                                    <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                                    Guardando...
                                </>
                            ) : (
                                'Restablecer Contraseña'
                            )}
                        </Button>
                    </Modal.Footer>
                </Form>
            )}
        </Modal>
    );
};

export default AuthPickupModal;
